/**
 * Changing what one person in a clinic may reach.
 *
 * Membership used to be a thing you could only grant. A store user who moved
 * branch had to be removed and re-added; somebody promoted to clinic admin kept
 * the store rows underneath the wider grant; and nothing but the API could fix
 * a name typed wrong at registration. All three are the same edit, so they are
 * one dialog.
 *
 * The stores are a SET, not a list of changes. The dialog says what this person
 * should cover and the server makes that true - anything unticked is switched
 * off, anything ticked is on. Add/remove operations would leave the screen
 * describing a sequence rather than a state, and a half-applied sequence is
 * exactly the membership nobody can reason about at three in the morning.
 *
 * `role` and the stores are mutually exclusive on purpose: a clinic admin
 * already reaches every store, so ticking branches for one is a contradiction
 * the dialog does not offer.
 */
import { roleOptions, type TenantRoleName } from '../roles.ts';
import { useEffect, useState } from 'react';
import { del, patch } from '../api.ts';
import { Field } from './EditPanel.tsx';
import { ModalForm } from './Modal.tsx';
import { Select } from './Select.tsx';
import { StoreChecklist, type StoreOption } from './StoreChecklist.tsx';

export interface EditableMember {
  id: string;
  email: string | null;
  display_name: string;
  clinic_admin: boolean;
  /** CLINIC_ADMIN / STORE_ADMIN / STORE_USER, as the server reports it. */
  role?: string;
  stores: Array<{ id: string; name: string }>;
}

const roleOf = (member: EditableMember): TenantRoleName =>
  member.clinic_admin
    ? 'CLINIC_ADMIN'
    : member.role === 'STORE_ADMIN'
      ? 'STORE_ADMIN'
      : 'STORE_USER';

export function MemberEditor({
  clinicId,
  member,
  stores,
}: {
  clinicId: string;
  member: EditableMember;
  stores: StoreOption[];
}) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const [displayName, setDisplayName] = useState(member.display_name);
  const [role, setRole] = useState<TenantRoleName>(roleOf(member));
  const clinicAdmin = role === 'CLINIC_ADMIN';
  const [chosen, setChosen] = useState<string[]>(member.stores.map((store) => store.id));

  // Reopening starts from what is stored, never from an abandoned edit.
  useEffect(() => {
    if (!editing) return;
    setDisplayName(member.display_name);
    setRole(roleOf(member));
    setChosen(member.stores.map((store) => store.id));
  }, [editing, member]);

  return (
    <>
      <div className="chips">
        <button className="btn btn--ghost btn--sm" type="button" onClick={() => setEditing(true)}>
          編集
        </button>
        <button className="btn btn--ghost btn--sm" type="button" onClick={() => setRemoving(true)}>
          解除
        </button>
      </div>

      <ModalForm
        open={editing}
        onClose={() => setEditing(false)}
        title="ユーザーの権限を変更"
        caption={member.email ?? undefined}
        submitLabel="保存する"
        onSubmit={async () => {
          if (!clinicAdmin && chosen.length === 0) {
            throw new Error('担当店舗を1つ以上選ぶか、クリニック管理に変更してください。');
          }
          await patch(`/admin/clinics/${clinicId}/members/${member.id}`, {
            displayName,
            role,
            ...(clinicAdmin ? {} : { storeIds: chosen }),
          });
        }}
      >
        <Field label="氏名" htmlFor={`m-name-${member.id}`} span="full">
          <input
            id={`m-name-${member.id}`}
            maxLength={200}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>

        <Field label="権限" htmlFor={`m-role-${member.id}`}>
          <Select
            id={`m-role-${member.id}`}
            value={role}
            onChange={(next) => setRole(next as TenantRoleName)}
            options={roleOptions(true)}
          />
        </Field>

        {/*
          Only for a store user. A clinic admin reaches every store already, so
          a list of branches beside that grant would be describing a limit that
          does not exist.
        */}
        {!clinicAdmin && (
          <StoreChecklist
            idPrefix={`m-stores-${member.id}`}
            stores={stores}
            value={chosen}
            onChange={setChosen}
          />
        )}

        {clinicAdmin && member.stores.length > 0 && (
          <p className="notice field--full">
            クリニック管理に変更すると、現在の担当店舗（
            {member.stores.map((store) => store.name).join('・')}
            ）の指定は解除されます。全店舗を扱えるようになります。
          </p>
        )}

        <p className="muted small field--full" style={{ marginBottom: 0 }}>
          保存すると、このユーザーのログイン中のセッションは一度終了します。次回のログインから新しい権限になります。
        </p>
      </ModalForm>

      <ModalForm
        open={removing}
        onClose={() => setRemoving(false)}
        title="このクリニックから外しますか"
        caption={`${member.display_name || member.email || ''}：所属を解除すると、このクリニックの記事は開けなくなります。アカウント自体は残ります。`}
        submitLabel="解除する"
        danger
        onSubmit={() => del(`/admin/clinics/${clinicId}/members/${member.id}`)}
      >
        <p className="field--full" style={{ margin: 0 }}>
          ログイン中のセッションは直ちに終了します。
        </p>
      </ModalForm>
    </>
  );
}
